// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@guestsupply/App/Components/Cart/CartLineContainer";
import CartLinePromoLabel from "@guestsupply/App/Components/Cart/CartLinePromoLabel";
import ProductBadges from "@guestsupply/App/Components/Common/ProductBadges";
import Notify from "@guestsupply/App/Components/Notify";
import setShipFromAltDCModalIsOpen from "@guestsupply/App/Handlers/BackOrders/SetShipFromAltDCModalIsOpen";
import xUseWidgetBadges from "@guestsupply/App/Hooks/xUseWidgetBadges";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import ProductAvailability, {
    AdditionalAvailabilityInfoModel,
    ProductAvailabilityStyles,
} from "@guestsupply/Overrides/Components/ProductAvailability";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@guestsupply/Overrides/Components/ProductPartNumbers";
import { shippingWeightUnit } from "@guestsupply/XDL/constants";
import ShippingWeight from "@guestsupply/XDL/Icons/ShippingWeight";
import SuccessAlert from "@guestsupply/XDL/Icons/SuccessAlert";
import { parseBoolean } from "@guestsupply/XDL/Utilities/helpers";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useCallback, useContext, useEffect } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine?: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine?: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
    // XNG-CHANGES
    showGroups?: boolean;
    editableQuantity?: boolean;
    showEligibleIcon?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    setShipFromAltDCModalIsOpen,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    // XNG-CHANGES
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    smallHeadingStyle?: SmallHeadingAndTextStyles;
    lineContainerStyles?: CartLineContainerStyles;
    shippingWeightWrapper?: InjectableCss;
    surchargesTooltip?: TooltipPresentationProps;
    totalFreightChargesLink?: LinkPresentationProps;
    headlineTypography?: TypographyProps;
    pricingFailedStyle?: TypographyProps;
    altDcLink?: LinkPresentationProps;
    notify?: TypographyProps;
    tooltipStyle?: TooltipPresentationProps;
    productImageBadgesStyle?: ProductImageBadgesStyles;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    binInformationsGridItem: {
        width: 12,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = false,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showVmiBinDetails,
    // XNG-CHANGES
    showGroups = false,
    showPricing,
    editableQuantity = true,
    setShipFromAltDCModalIsOpen,
    showEligibleIcon,
    isAuthenticated,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    // XNG-CHANGES
    const additionalAvailabilityInfo =
        cartLine?.properties?.additionalAvailabilityInfo &&
        JSON.parse(cartLine?.properties?.additionalAvailabilityInfo);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0 && removeCartLine) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine &&
                    updateCartLine({
                        cartLine: {
                            ...cartLine,
                            qtyOrdered,
                        },
                    });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode && updateCartLine) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes && updateCartLine) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        removeCartLine && removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine &&
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        productSubscription: JSON.stringify(updatedSubscription),
                    },
                },
            });
    };

    const handleShipFromPrimaryDC = () => {
        updateCartLine &&
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        removeSelectedAltWarehouse: "true",
                    },
                },
            });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    const { ootbBadgesAndTags, customBadgesAndTags } = xUseWidgetBadges({
        productContext: { product: cartLine, productInfo: {} } as unknown as ProductContextModel,
        showTextBadges: false,
        showImageBadges: true,
    });

    const calculateSavedValue =
        cartLine.pricing &&
        cartLine.pricing.unitListPriceDisplay.charAt(0) +
            ((cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice) * (cartLine.qtyOrdered ?? 0)).toFixed(2);

    // xng-change
    const getLinePromotion = useCallback(
        () => promotions?.find(promo => promo.orderLineId === cartLine.id),
        [promotions, cartLine],
    );

    return (
        <CartLineContainer extendedStyles={styles.lineContainerStyles} className={`${showGroups && "group-cartline"}`}>
            <CartLineContainer.Promotion>
                <CartLinePromoLabel promo={getLinePromotion()} />
            </CartLineContainer.Promotion>
            {showImage && (
                <CartLineContainer.Image>
                    <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                </CartLineContainer.Image>
            )}
            <CartLineContainer.Description>
                {showBrand && cartLine.brand && (
                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                )}
                {showProductTitle && (
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                )}
                {!cartLine.isFixedConfiguration &&
                    cartLine.sectionOptions!.length > 0 &&
                    cartLine.sectionOptions!.map(option => (
                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                            {`${option.sectionName}:${option.optionName}`}
                        </Typography>
                    ))}
                <ProductPartNumbers
                    productNumber={cartLine.erpNumber}
                    customerProductNumber={cartLine.customerName}
                    manufacturerItem={cartLine.manufacturerItem}
                    extendedStyles={styles.productPartNumbers}
                    showProductNumber={showProductNumber}
                    // XNG-changes
                    packDescription={cartLine.properties.packDescription}
                    shippingWeight={cartLine.properties.shippingWeight}
                    showEligibleIcon={showGroups && showEligibleIcon && isAuthenticated}
                />
                {showVmiBinDetails && currentMode === "Vmi" && (
                    <GridItem {...styles.binInformationsGridItem}>
                        <ProductBinNumbers
                            locationId={cart.vmiLocationId}
                            binId={cartLine.vmiBinId}
                            isCondensed={false}
                            extendedStyles={styles.productBinNumbers}
                        />
                    </GridItem>
                )}
                {/* {promotions.map(promotion => (
                    <Typography {...styles.promotionNameText} key={promotion.id}>
                        {promotion.name}
                    </Typography>
                ))} */}
                {/* xng-change */}
                {/* {imageBadges && (
                    <ProductImageBadges
                        badgeList={imageBadges}
                        maxBadges={4}
                        extendedStyles={styles.productImageBadgesStyle}
                    />
                )} */}
                <ProductBadges ootbBadgesAndTags={ootbBadgesAndTags} customBadgesAndTags={customBadgesAndTags} />
            </CartLineContainer.Description>
            <CartLineContainer.Price>
                {!cart.cartNotPriced ? (
                    <>
                        <Typography {...styles.headlineTypography}>{translate("Price/Unit")}</Typography>
                        <ProductPrice
                            product={cartLine}
                            currencySymbol={cart.currencySymbol}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            showInvalidPriceMessage={false}
                            // XNG-CHANGES
                            showLabel={false}
                            extendedStyles={styles.productPrice}
                        />

                        {showPricing &&
                            promotions.map(promotion => (
                                <SmallHeadingAndText
                                    key={promotion.id}
                                    heading={translate("You Saved")}
                                    text={promotion.amountDisplay}
                                    extendedStyles={styles.smallHeadingStyle}
                                />
                            ))}
                        {promotions.length === 0 &&
                            cartLine?.pricing &&
                            cartLine?.pricing?.unitNetPrice < cartLine?.pricing?.unitListPrice && (
                                <SmallHeadingAndText
                                    heading={translate("You Saved")}
                                    text={calculateSavedValue ?? 0}
                                    extendedStyles={styles.smallHeadingStyle}
                                />
                            )}
                        {/* <ProductQuantityBreakPricing /> */}
                    </>
                ) : (
                    <Typography {...styles.pricingFailedStyle}>
                        {siteMessage("RealTimePricing_PriceLoadFailed")}
                    </Typography>
                )}
                {cartLine?.properties?.totalFreightCharges && cartLine.properties.totalFreightCharges !== "$0.00" && (
                    <Tooltip
                        {...styles.surchargesTooltip}
                        text={siteMessage("GUEST_Surcharges_Icon_Hover", "This product has surcharges") as string}
                        triggerComponent={
                            <Link
                                {...styles.totalFreightChargesLink}
                                data-test-selector="totalFreightCharges"
                                aria-label={cartLine?.properties?.totalFreightCharges}
                            >
                                {cartLine?.properties?.totalFreightCharges}
                            </Link>
                        }
                    />
                )}
                {showInventoryAvailability && !cartLine.quoteRequired && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        altDc={cartLine.properties.availableAltWarehouse}
                        altDcSelected={cartLine.properties.altWarehouseInfo}
                        additionalAvailabilityInfo={additionalAvailabilityInfo as AdditionalAvailabilityInfoModel}
                        extendedStyles={styles.productAvailability}
                    />
                )}
                {cartLine.properties.availableAltWarehouse && (
                    <Tooltip
                        {...styles.tooltipStyle}
                        // XNG-CHANGES: GSS-984 : hiding other tooltip in the cartline when triggering the altDC
                        {...{
                            onTrigger: () => {
                                hideAll();
                            },
                        }}
                        text={
                            siteMessage(
                                "GUEST_Cart_Alt_DC_Button_Message",
                                "Available to ship from an alternate DC. Processing fee will be assessed when adding to a cart",
                            ) as string
                        }
                        triggerComponent={
                            <Link
                                {...styles.altDcLink}
                                onClick={() => setShipFromAltDCModalIsOpen({ modalIsOpen: true, backorder: cartLine })}
                            >
                                {siteMessage("GUEST_Cart_Alt_DC_Button")}
                            </Link>
                        }
                    />
                )}
                {cartLine.properties.altWarehouseInfo && (
                    <Tooltip
                        {...styles.tooltipStyle}
                        // XNG-CHANGES: GSS-984 : hiding other tooltip in the cartline when triggering the altDC
                        {...{
                            onTrigger: () => {
                                hideAll();
                            },
                        }}
                        text={
                            siteMessage(
                                "GUEST_Cart_Primary_DC_Button_Message",
                                "Shipping from the primary DC will change the cart back to have a backorder rail",
                            ) as string
                        }
                        triggerComponent={
                            <Link {...styles.altDcLink} onClick={handleShipFromPrimaryDC}>
                                {siteMessage("GUEST_Cart_Primary_DC")}
                            </Link>
                        }
                    />
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity
                    cart={cart}
                    editable={editableQuantity}
                    onQtyOrderedChange={qtyOrderedChangeHandler}
                    extendedStyles={styles.quantity}
                    label={translate("Qty")}
                />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                    <>
                        <Typography {...styles.headlineTypography}>{translate("Total")}</Typography>
                        <Typography
                            {...styles.extendedUnitNetPriceText}
                            data-test-selector="cartline_extendedUnitNetPrice"
                        >
                            {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                : cartLine.pricing.extendedUnitNetPriceDisplay}
                        </Typography>
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.subtotalWithoutVatText}>
                                            {cartLine.pricing.extendedUnitNetPriceDisplay}
                                        </Typography>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </CartLineContainer.Total>
            <CartLineContainer.Remove>
                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine">
                        <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                        <IconMemo {...styles.removeCartLineIcon} />
                    </Clickable>
                )}
                {cartLine.isSubscription && cartLine.productSubscription && (
                    <ProductDeliveryScheduleButton
                        subscription={
                            cartLine.properties.productSubscription
                                ? JSON.parse(cartLine.properties.productSubscription)
                                : cartLine.productSubscription
                        }
                        disabled={
                            !cart.canModifyOrder ||
                            cartLine.isPromotionItem ||
                            !cartLine.qtyOrdered ||
                            cartLine.qtyOrdered <= 0
                        }
                        onSave={saveScheduleHandler}
                        extendedStyles={styles.deliveryScheduleButton}
                    />
                )}
                {cart.showCostCode && !cartLine.isPromotionItem && (
                    <>
                        <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                        {cart.canEditCostCode ? (
                            <Select
                                value={cartLine.costCode}
                                onChange={costCodeChangeHandler}
                                aria-label={translate("Cost Code")}
                                {...styles.costCodeSelect}
                            >
                                {cart.costCodes?.map(costCode => (
                                    <option key={costCode.costCode} value={costCode.costCode}>
                                        {costCode.description}
                                    </option>
                                ))}
                            </Select>
                        ) : (
                            <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                        )}
                    </>
                )}
            </CartLineContainer.Remove>

            <CartLineContainer.Actions>
                {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                    <Link {...styles.addToListLink} onClick={addToListClickHandler} aria-label="Add to List">
                        {translate("Add to List")}
                    </Link>
                )}
                {showLineNotes && !cartLine.isPromotionItem && !parseBoolean(cart.properties["isPunchout"]) && (
                    <CartLineNotes
                        cart={cart}
                        editable={true}
                        onNotesChange={notesChangeHandler}
                        extendedStyles={styles.cartLineNotes}
                    />
                )}
                {errorMessages.map((message, index) => (
                    <Notify
                        {...styles.notify}
                        type="danger"
                        data-test-selector="cartline_errorMessage"
                        key={`error_${cartLine.id}`}
                    >
                        {message}
                    </Notify>
                ))}
            </CartLineContainer.Actions>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
